import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import { toast } from "sonner";
import {
  driverAvailabilityApi,
  type CheckDriverAvailabilityRequest,
  type CreateDriverAvailabilityRequest,
  type DriverCalendarOverviewQuery,
  type DriverAvailabilityListQuery,
  type PreviewDriverAvailabilityImpactRequest,
  type UpdateDriverAvailabilityRequest,
} from "../services/driverAvailabilityApi";

const QK = {
  list: (query: DriverAvailabilityListQuery) => ["driver-availability", query],
  calendar: (query: DriverCalendarOverviewQuery) => ["driver-availability-calendar", query],
} as const;

export const useDriverAvailabilityPaged = (query: DriverAvailabilityListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => driverAvailabilityApi.getPaged(query),
  });

export const useDriverAvailabilityCalendar = (query: DriverCalendarOverviewQuery) =>
  useQuery({
    queryKey: QK.calendar(query),
    queryFn: () => driverAvailabilityApi.getCalendarOverview(query),
  });

export const useCreateDriverAvailability = () =>
  useMutation({
    mutationFn: (data: CreateDriverAvailabilityRequest) => driverAvailabilityApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["driver-availability"] });
      queryClient.invalidateQueries({ queryKey: ["driver-availability-calendar"] });
      toast.success("Driver availability saved.");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to save availability.");
    },
  });

export const useUpdateDriverAvailability = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDriverAvailabilityRequest }) =>
      driverAvailabilityApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["driver-availability"] });
      queryClient.invalidateQueries({ queryKey: ["driver-availability-calendar"] });
      toast.success("Driver availability updated.");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to update availability.");
    },
  });

export const useDeactivateDriverAvailability = () =>
  useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      driverAvailabilityApi.deactivate(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["driver-availability"] });
      queryClient.invalidateQueries({ queryKey: ["driver-availability-calendar"] });
      toast.success("Driver availability deactivated.");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to deactivate availability.");
    },
  });

export const useCheckDriverAvailability = () =>
  useMutation({
    mutationFn: (payload: CheckDriverAvailabilityRequest) => driverAvailabilityApi.check(payload),
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to check driver availability.");
    },
  });

export const usePreviewDriverAvailabilityImpacts = () =>
  useMutation({
    mutationFn: (payload: PreviewDriverAvailabilityImpactRequest) =>
      driverAvailabilityApi.previewImpacts(payload),
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to preview availability impacts.");
    },
  });
